import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSearchContext } from '../contexts/SearchContext';
import SearchBar from './SearchBar';

interface SearchSummaryBarProps {
  country: string; // Assuming country is of type string
  type: string;    // Assuming type is of type string
}

const SearchSummaryBar = forwardRef(({ country, type }: SearchSummaryBarProps, ref) => {
  const { checkIn, checkOut, adultCount, childCount, nights } = useSearchContext();
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [hover, setHover] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(ref, () => ({
    triggerEditSearchClick() {
      if (editButtonRef.current) {
        editButtonRef.current.click();
      }
    },
  }));

  // Path to the regular and hover state SVGs
  const EditIcon = '/featureIcons/editing.svg'; // Default SVG path
  const EditIconHover = '/featureIcons/editing-white.svg';

  // Format the dates to a readable string
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(date));
  };

  // Handle scroll event to toggle sticky class
  useEffect(() => {
    const handleScroll = () => {
      const stickyThreshold = 100; // You can adjust this threshold as needed
      if (window.innerWidth <= 768) { // Check if the screen width is 768px or less (mobile view)
        if (window.scrollY > stickyThreshold) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      } else {
        setIsSticky(false); // Ensure it's not sticky on larger screens
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Scroll to the top and show the search bar
  const handleEditSearchClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setShowSearchBar(true);
  };

  return (
    <div>
      {/* Spacer for sticky bar to avoid content jumping */}
      <div
        className={isSticky ? "spacer" : "hidden"}
        style={{ height: "10rem" }}
      />
      {!showSearchBar ? (
        <div
          className={`bg-white shadow rounded-lg p-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-7 gap-4 items-center mb-4 transition-all duration-300 ease-in-out ${
            isSticky ? "sticky-bar sticky-on-mobile" : ""
          }`}
        >
          {!isSticky && (
            <>
              <div className="sm:col-span-1">
                <p className="text-sm text-gray-500">GOING TO</p>
                <p className="font-semibold">{country}</p>
              </div>
              <div className="sm:col-span-1">
                <p className="text-sm text-gray-500">TYPE</p>
                <p className="font-semibold">{type}</p>
              </div>
              <div className="sm:col-span-1">
                <p className="text-sm text-gray-500">DEPART</p>
                <p className="font-semibold">{formatDate(checkIn)}</p>
              </div>
              <div className="sm:col-span-1">
                <p className="text-sm text-gray-500">RETURN</p>
                <p className="font-semibold">{formatDate(checkOut)}</p>
              </div>
              <div className="sm:col-span-1">
                <p className="text-sm text-gray-500">NIGHTS</p>
                <p className="font-semibold">{nights}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-gray-500">ROOM & GUESTS</p>
                <p className="font-semibold">{`${adultCount} Adults, ${childCount} Children`}</p>
              </div>
            </>
          )}
          <div className="sm:col-span-2 lg:col-span-1">
            <button
              ref={editButtonRef}
              className="flex items-center justify-center space-x-2 text-halo-pink bg-white border border-halo-pink hover:bg-halo-pink hover:text-white transition-colors px-4 py-2 rounded w-full"
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              onClick={handleEditSearchClick}
            >
              <img
                src={hover ? EditIconHover : EditIcon}
                alt="Edit"
                className="w-4 h-4"
              />
              <span className="mt-1">Edit search</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="-mt-24 mb-12">
          <SearchBar />
          <button
            className="text-halo-pink transition-colors px-3 py-0 rounded mt-10 search-close"
            onClick={() => setShowSearchBar(false)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
});

export default SearchSummaryBar;
